import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { brandInitial as toBrandInitial, isValidWorkEmail } from '../../../onboarding-value-first/services/value-first-onboarding.types';
import { ValueFirstAgencyService } from '../../services/value-first-agency.service';
import { AgencyStep } from '../../services/value-first-agency.types';

@Component({
  selector: 'vfa-workspace-panel',
  templateUrl: './workspace-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'hidden lg:block lg:w-1/2' },
})
export class VfaWorkspacePanel {
  readonly onboarding = inject(ValueFirstAgencyService);
  readonly slots = [0, 1, 2];

  readonly filled = computed(() => {
    const step = this.onboarding.step();
    return step !== 'website' && step !== 'manual';
  });

  readonly label = computed(() => {
    const step = this.onboarding.step();
    if (step === 'welcome') {
      return 'Your agency workspace is live';
    }
    if (step === 'website' || step === 'manual') {
      return 'Your agency workspace appears here';
    }
    return 'Your agency workspace';
  });

  readonly ownerLabel = computed(() => {
    const step = this.onboarding.step();
    const draft = this.onboarding.draft();
    if (step === 'welcome') {
      const name = `${draft.firstName} ${draft.lastName}`.trim();
      return name ? `Owner: ${name}` : 'Owner';
    }
    if ((step === 'account' || step === 'code') && isValidWorkEmail(draft.email)) {
      return `Reserved for ${draft.email.trim()}`;
    }
    return 'Not claimed yet';
  });

  readonly status = computed(() => this.statusFor(this.onboarding.step(), this.onboarding.draft().email));

  initial(): string {
    return toBrandInitial(this.onboarding.agencyName());
  }

  brandInitial(name: string): string {
    return toBrandInitial(name);
  }

  domainLabel(domain: string): string {
    return domain || 'Added manually';
  }

  slotBrand(slot: number) {
    if (!this.filled()) {
      return undefined;
    }
    return this.onboarding.draft().brands[slot];
  }

  brandCountLabel(): string {
    const count = this.onboarding.draft().brands.length;
    return count === 1 ? '1 brand' : `${count} brands`;
  }

  private statusFor(step: AgencyStep, email: string): { label: string; live: boolean } {
    if (step === 'welcome') {
      return { label: 'Live', live: true };
    }
    if (step === 'found' || step === 'brands' || ((step === 'account' || step === 'code') && isValidWorkEmail(email))) {
      return { label: 'Reserved', live: false };
    }
    return { label: 'Draft', live: false };
  }
}
